import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2,
  FileText,
  Shield,
  Video,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { consentService, ConsentTemplate, ConsentFormType } from '@/services/consentService';
import ConsentSignature, { SignatureData } from './ConsentSignature';

const FORM_TYPE_ICONS: Record<string, React.ReactNode> = {
  telehealth: <Video className='h-4 w-4' />,
  hipaa: <Shield className='h-4 w-4' />,
  financial: <FileText className='h-4 w-4' />,
  treatment: <FileText className='h-4 w-4' />,
  custom: <FileText className='h-4 w-4' />,
};

const FORM_TYPE_COLORS: Record<string, string> = {
  telehealth: 'bg-blue-100 text-blue-800',
  hipaa: 'bg-purple-100 text-purple-800',
  financial: 'bg-green-100 text-green-800',
  treatment: 'bg-orange-100 text-orange-800',
  custom: 'bg-gray-100 text-gray-800',
};

interface MissingConsent {
  id: string;
  name: string;
  form_type: ConsentFormType;
}

interface ConsentGateProps {
  /** Clinic ID to check consents for */
  clinicId: string;
  /** Patient ID to check consents for */
  patientId: string;
  /** Patient name for signature display */
  patientName?: string;
  /** Purpose of the consent check - determines which required consents to check */
  purpose: 'booking' | 'telehealth';
  /** Called when all required consents are complete */
  onComplete: () => void;
  /** Optional: Called when user cancels */
  onCancel?: () => void;
  /** Optional: Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Optional: Show as inline component instead of blocking gate */
  inline?: boolean;
  /** Children to render when consents are complete (for inline mode) */
  children?: React.ReactNode;
  /** Optional: If true, automatically calls onComplete when all consents are already signed on mount. Defaults to true. */
  allowAutoCompleteOnMount?: boolean;
}

/**
 * ConsentGate Component
 *
 * Checks if a patient has all required consent forms signed.
 * If missing consents exist, prompts the patient to sign them before proceeding.
 *
 * Usage:
 * ```tsx
 * <ConsentGate
 *   clinicId={clinicId}
 *   patientId={patientId}
 *   purpose="telehealth"
 *   onComplete={() => joinTelehealthCall()}
 *   onCancel={() => navigate('/appointments')}
 * />
 * ```
 */
const ConsentGate: React.FC<ConsentGateProps> = ({
  clinicId,
  patientId,
  patientName,
  purpose,
  onComplete,
  onCancel,
  loadingComponent,
  inline = false,
  children,
  allowAutoCompleteOnMount = true,
}) => {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [missingConsents, setMissingConsents] = useState<MissingConsent[]>([]);
  const [currentConsentIndex, setCurrentConsentIndex] = useState(0);
  const [currentTemplate, setCurrentTemplate] = useState<ConsentTemplate | null>(null);
  const [showSigningDialog, setShowSigningDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasReadConsent, setHasReadConsent] = useState(false);
  const [allComplete, setAllComplete] = useState(false);
  // React Strict Mode + prop-dep changes can re-run the check effect. Fire
  // onComplete() at most once per mount to avoid duplicate downstream calls
  // (e.g. two /confirm POSTs that deadlock on patient_action_links).
  const autoCompleteFiredRef = useRef(false);

  // Check for missing consents on mount
  useEffect(() => {
    checkConsents();
  }, [clinicId, patientId, purpose]);

  const checkConsents = async () => {
    if (!clinicId || !patientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await consentService.checkPatientConsents(clinicId, patientId, purpose);

      if (response.success && response.data) {
        if (response.data.hasAll) {
          setAllComplete(true);
          if (allowAutoCompleteOnMount && !autoCompleteFiredRef.current) {
            autoCompleteFiredRef.current = true;
            onComplete();
          }
        } else {
          setMissingConsents(response.data.missing);
          setCurrentConsentIndex(0);
        }
      }
    } catch (error: any) {
      console.error('Failed to check consents:', error);
      toast({
        title: 'Error',
        description: 'Failed to check consent requirements',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentConsent = async (indexOverride?: number) => {
    const index = indexOverride ?? currentConsentIndex;

    if (index >= missingConsents.length) {
      // All consents completed
      setAllComplete(true);
      setShowSigningDialog(false);
      onComplete();
      return;
    }

    const consentToSign = missingConsents[index];

    try {
      setChecking(true);
      const response = await consentService.getPublicConsentTemplate(consentToSign.id);

      if (response.success && response.data) {
        setCurrentTemplate(response.data);
        setHasReadConsent(false);
        setShowSigningDialog(true);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load consent form',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Failed to load consent template:', error);
      toast({
        title: 'Error',
        description: 'Failed to load consent form',
        variant: 'destructive',
      });
    } finally {
      setChecking(false);
    }
  };

  const handleStartSigning = () => {
    loadCurrentConsent();
  };

  const handleSignature = async (signatureData: SignatureData) => {
    if (!currentTemplate) return;

    try {
      setSubmitting(true);

      const response = await consentService.submitPublicConsentSignature({
        patient_id: patientId,
        clinic_id: clinicId,
        consent_form_template_id: currentTemplate.id,
        signature_type: signatureData.type,
        signature_data: signatureData,
      });

      if (response.success) {
        toast({
          title: 'Consent Signed',
          description: `${currentTemplate.name} signed successfully`,
        });

        // Move to next consent
        const nextIndex = currentConsentIndex + 1;
        setCurrentConsentIndex(nextIndex);

        if (nextIndex >= missingConsents.length) {
          // All done
          setAllComplete(true);
          setShowSigningDialog(false);
          onComplete();
        } else {
          // Load next consent - pass nextIndex directly since state update is async
          setCurrentTemplate(null);
          setHasReadConsent(false);
          loadCurrentConsent(nextIndex);
        }
      }
    } catch (error: any) {
      console.error('Failed to submit consent:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit consent',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowSigningDialog(false);
    if (onCancel) {
      onCancel();
    }
  };

  // Loading state
  if (loading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div className='flex items-center justify-center p-8'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  // All consents complete - render children in inline mode
  if (allComplete) {
    if (inline && children) {
      return <>{children}</>;
    }
    return null;
  }

  // No missing consents initially
  if (missingConsents.length === 0) {
    if (inline && children) {
      return <>{children}</>;
    }
    return null;
  }

  const progress = (currentConsentIndex / missingConsents.length) * 100;
  const purposeLabel = purpose === 'telehealth' ? 'telehealth call' : 'appointment booking';

  // Consent gate UI
  return (
    <>
      <Card className='mx-auto max-w-2xl'>
        <CardHeader>
          <div className='mb-2 flex items-center gap-2 text-amber-600'>
            <AlertTriangle className='h-5 w-5' />
            <span className='text-sm font-medium'>Action Required</span>
          </div>
          <CardTitle>Consent Forms Required</CardTitle>
          <CardDescription>
            Before proceeding with your {purposeLabel}, please review and sign the following consent
            form{missingConsents.length > 1 ? 's' : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Progress */}
          {missingConsents.length > 1 && (
            <div className='space-y-2'>
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground'>Progress</span>
                <span className='font-medium'>
                  {currentConsentIndex} of {missingConsents.length} completed
                </span>
              </div>
              <Progress value={progress} className='h-2' />
            </div>
          )}

          {/* Missing consents list */}
          <div className='space-y-2'>
            {missingConsents.map((consent, index) => {
              const isComplete = index < currentConsentIndex;
              const isCurrent = index === currentConsentIndex;
              const typeColor = FORM_TYPE_COLORS[consent.form_type] || FORM_TYPE_COLORS.custom;

              return (
                <div
                  key={consent.id}
                  className={'flex items-center justify-between rounded-lg border bg-muted/50 p-3'}
                >
                  <div className='flex items-center gap-3'>
                    {isComplete ? (
                      <CheckCircle className='h-5 w-5 text-green-600' />
                    ) : (
                      <div className={`rounded p-1.5 ${typeColor}`}>
                        {FORM_TYPE_ICONS[consent.form_type]}
                      </div>
                    )}
                    <div>
                      <p className={`font-medium ${isComplete ? 'text-green-800' : ''}`}>
                        {consent.name}
                      </p>
                      <Badge variant='outline' className={`text-xs ${typeColor}`}>
                        {consent.form_type}
                      </Badge>
                    </div>
                  </div>
                  {isComplete && (
                    <Badge
                      variant='outline'
                      className='border-green-200 bg-green-100 text-green-800'
                    >
                      Signed
                    </Badge>
                  )}
                  {/* {isCurrent && !showSigningDialog && (
                    <ChevronRight className='h-5 w-5 text-primary' />
                  )} */}
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className='flex gap-3 pt-4'>
            {onCancel && (
              <Button variant='outline' onClick={handleCancel} className='flex-1'>
                Cancel
              </Button>
            )}
            <Button
              onClick={handleStartSigning}
              disabled={checking}
              className='flex-1 disabled:cursor-not-allowed'
            >
              {checking ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Loading...
                </>
              ) : (
                <>
                  Start Signing
                  <ChevronRight className='ml-2 h-4 w-4' />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Signing Dialog */}
      <Dialog open={showSigningDialog} onOpenChange={open => setShowSigningDialog(open)}>
        <DialogContent className='max-h-[90vh] max-w-3xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              {currentTemplate && FORM_TYPE_ICONS[currentTemplate.form_type]}
              {currentTemplate?.name || 'Consent Form'}
            </DialogTitle>
            <DialogDescription>
              {missingConsents.length > 1 && (
                <span>
                  Form {currentConsentIndex + 1} of {missingConsents.length} •{' '}
                </span>
              )}
              Please read and sign below
            </DialogDescription>
          </DialogHeader>

          {currentTemplate ? (
            <div className='space-y-6'>
              {/* Consent Content */}
              <div
                className='prose prose-sm dark:prose-invert max-h-[40vh] max-w-none overflow-y-auto rounded-lg border bg-muted/30 p-4 [&>h2]:mb-2 [&>h2]:mt-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h3]:mb-2 [&>h3]:mt-3 [&>h3]:text-base [&>h3]:font-medium [&>li]:mb-1 [&>ol]:mb-3 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:text-sm [&>p]:mb-3 [&>p]:text-sm [&>ul]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:text-sm'
                dangerouslySetInnerHTML={{ __html: currentTemplate.content }}
              />

              {/* Acknowledgement */}
              <Alert>
                <AlertTitle className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    id='consent-ack'
                    checked={hasReadConsent}
                    onChange={e => setHasReadConsent(e.target.checked)}
                    className={`h-4 w-4 ${submitting ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    disabled={submitting}
                  />
                  <label
                    htmlFor='consent-ack'
                    className={submitting ? 'cursor-not-allowed' : 'cursor-pointer'}
                  >
                    I have read and understand this consent form
                  </label>
                </AlertTitle>
                <AlertDescription className='ml-6'>
                  By signing below, I provide my informed consent and agree to the terms described
                  above.
                </AlertDescription>
              </Alert>

              {/* Signature */}
              {currentTemplate.signature_required ? (
                <ConsentSignature
                  onSign={handleSignature}
                  disabled={!hasReadConsent || submitting}
                  isconsentread={hasReadConsent}
                  patientName={patientName}
                />
              ) : (
                <Button
                  onClick={() =>
                    handleSignature({
                      type: 'typed',
                      data: 'Acknowledged',
                      timestamp: new Date().toISOString(),
                    })
                  }
                  disabled={!hasReadConsent || submitting}
                  className='w-full disabled:cursor-not-allowed'
                >
                  {submitting ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className='mr-2 h-4 w-4' />I Agree
                    </>
                  )}
                </Button>
              )}
            </div>
          ) : (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConsentGate;
